'use client';

import { ArrowRight, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { Link } from '@/i18n/routing';

/**
 * In-app announcement for the Spisy Trial for 100 campaign (#8894).
 *
 * A full-bleed gradient strip pinned to the top of the app shell, above the
 * header: badge, one line of copy, an inline CTA, and a dismiss control at the
 * trailing edge.
 *
 * Two layout details are load-bearing. The copy is centred by the row's own
 * `justify-center` while the close button is absolutely positioned, so the two
 * cannot fight — an earlier pass had `justify-center` on the row AND `ml-auto`
 * on the button, which collapsed the copy to the left and stranded the X at the
 * far edge. And the strip is in normal flow rather than `fixed`, so it displaces
 * the header instead of covering app chrome.
 *
 * ## Gating
 *
 * `published` is `isAdvocateLandingPublished()`, resolved in the SERVER layout
 * and passed down — `lib/compliance/advocate-landing` must never reach a client
 * bundle (it reads server-only env, and inlining it would leak the gate's state
 * to every visitor).
 *
 * The PUBLISHED gate, not the reachability one, because this banner is an
 * announcement surface: it links to the landing page exactly like the header
 * and footer nav links, which read the same gate. `isAdvocateLandingPublished()`
 * implies `isAdvocateLandingEnabled()`, so the banner can never advertise a
 * route that 404s. Reusing that flag rather than minting a new one keeps the
 * banner and its CTA target on one switch by construction.
 *
 * ## Dismissal
 *
 * Persisted in localStorage and read in an effect rather than during render:
 * localStorage does not exist on the server, so an inline read would be a
 * hydration mismatch. Until that effect runs the banner stays unrendered, which
 * also stops it flashing in for someone who dismissed it long ago.
 */

const DISMISSED_STORAGE_KEY = 'lexomat.spisyTrialBanner.dismissed';

interface SpisyTrialAnnouncementBannerProps {
  /** Resolved `isAdvocateLandingPublished()`. False keeps the banner unmounted. */
  published: boolean;
}

export function SpisyTrialAnnouncementBanner({
  published,
}: SpisyTrialAnnouncementBannerProps) {
  const t = useTranslations('SpisyTrialBanner');
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!published) return;
    try {
      setDismissed(
        window.localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
      );
    } catch {
      // Private mode / storage disabled — show the banner rather than hide it.
      setDismissed(false);
    }
  }, [published]);

  function handleDismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
    } catch {
      // Dismissal simply doesn't persist; the banner still closes for now.
    }
  }

  if (!published || dismissed) return null;

  return (
    <div
      role='region'
      aria-label={t('ariaLabel')}
      // text-cta-text is the one foreground token that stays solid white across
      // all eight palette/mode combinations (#8795), which is what this gradient
      // needs — text-content-inverse flips to near-black in the dark palettes.
      className='relative w-full [background:var(--gradient-button)] text-cta-text'
    >
      {/* pr-12 reserves the absolutely-positioned close button's column so long
          copy can never slide underneath it. */}
      <div className='mx-auto flex min-h-11 max-w-7xl flex-wrap items-center justify-center gap-x-2.5 gap-y-1 px-4 py-2 pr-12 text-center'>
        <span className='rounded-full bg-black/25 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider'>
          {t('badge')}
        </span>

        <p className='text-sm'>
          <span className='font-semibold'>{t('title')}</span>{' '}
          <span className='opacity-90'>{t('body')}</span>
        </p>

        <Link
          href='/for-lawyers'
          className='inline-flex items-center gap-1 text-sm font-semibold underline decoration-current/60 underline-offset-4 transition hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current'
        >
          {t('cta')}
          <ArrowRight className='h-3.5 w-3.5' aria-hidden='true' />
        </Link>
      </div>

      <button
        type='button'
        onClick={handleDismiss}
        aria-label={t('dismiss')}
        className='absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md opacity-80 transition hover:bg-black/15 hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current'
      >
        <X className='h-4 w-4' aria-hidden='true' />
      </button>
    </div>
  );
}
